import { useState, useEffect, useRef, useCallback } from "react";
import { usePreload } from "../DataContext/PreloadContext";
import { buildQueryString } from "../../helpers/buildQueryString";
import {
  saveIncludedFeature,
  deleteIncludedFeature,
} from "../../services/includedFeatureService";

export const usePagedQuery = ({
  fetchFunction,
  defaultOptions = [],
  searchOption = [],
  cache,
  language,
  searchKey = "",
  pageSize = 50,
  deps = [],
  transform,
  captureErrors = false,
}) => {
  const { withLoader, confirmDialog, showNotification } = usePreload();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const [searchTerm, setSearchTerm] = useState(searchKey);
  const [searchType, setSearchType] = useState(searchOption[0]?.id);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [fetched, setFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const withLoaderRef = useRef(withLoader);

  useEffect(() => {
    withLoaderRef.current = withLoader;
  }, [withLoader]);

  const handleSearch = (value) => {
    cacheRef.current.clear();
    setPage(0);
    setSearchTerm(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch(e.target.value);
    }
  };

  const cacheRef = useRef(cache);
  const loadData = useCallback(
    async (force = false) => {
      if (!searchType) return;
      setFetched(false);
      if (captureErrors) setError(null);
      const key = JSON.stringify({
        language,
        page,
        rowsPerPage,
        searchType,
        search: searchTerm,
      });
      // Use cached data unless force is true
      if (!force) {
        const cached = cacheRef.current.get(key);
        if (cached) {
          setData(cached.data);
          setTotal(cached.total);
          setIsLoading(false);
          return;
        }
      }

      const query = buildQueryString({
        searchOn: searchType,
        language,
        page,
        rowsPerPage,
        searchTerm: !searchKey ? `*${searchTerm}*` : `${searchTerm}`,
      });
      setIsLoading(true);
      try {
        let requestFailed = false;
        await withLoaderRef.current(async () => {
          let res;
          try {
            res = await fetchFunction(query);
            if (captureErrors && res instanceof Response && !res.ok) {
              throw new Error(`Request failed (${res.status})`);
            }
          } catch (err) {
            if (!captureErrors) throw err;
            requestFailed = true;
            setError(err);
            setData([]);
            setTotal(0);
            return;
          }

          const processed = transform ? transform(res) : res;
          const result = {
            data: processed.results ?? [],
            total: processed.total ?? 0,
          };

          if (language?.toLowerCase() != "all")
            cacheRef.current.set(key, result);
          setData(result.data);
          setTotal(result.total);
        });
        if (!requestFailed) setFetched(true);
      } finally {
        setIsLoading(false);
      }
    },

    [
      language,
      page,
      rowsPerPage,
      fetchFunction,
      //withLoader,
      transform,
      searchTerm,
      searchType,
      captureErrors,
    ],
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // update row locally + cache
  const mutate = (updatedRow, idField = "id") => {
    const updateRows = (rows) =>
      rows.map((r) =>
        r[idField] === updatedRow[idField] ? { ...r, ...updatedRow } : r,
      );

    // update UI
    setData((prev) => updateRows(prev));

    // update cached pages
    const entries = [...cacheRef.current.cache];

    for (const [key, value] of entries) {
      const index = value.data.findIndex(
        (r) => r[idField] === updatedRow[idField],
      );

      if (index === -1) continue;

      const newData = [...value.data];
      newData[index] = { ...value.data[index], ...updatedRow };

      cacheRef.current.set(key, {
        ...value,
        data: newData,
      });
    }
  };

  const updateCachedItem = (updatedRow, idField = "id") => {
    const updateRows = (rows) =>
      rows.map((r) =>
        r[idField] === updatedRow[idField] ? { ...r, ...updatedRow } : r,
      );

    // Update current page UI
    // setData((prev) => updateRows(prev));

    // Update all cached pages
    const entries = [...cacheRef.current.cache];

    for (const [key, value] of entries) {
      const updatedData = updateRows(value.data);

      cacheRef.current.set(key, {
        ...value,
        data: updatedData,
      });
    }
  };

  const removeCachedItem = (rowToRemove, idField = "id") => {
    const removeRows = (rows) =>
      rows.filter((r) => r[idField] !== rowToRemove[idField]);

    // Update current page UI
    setData((prev) => removeRows(prev));

    // Update all cached pages
    const entries = [...cacheRef.current.cache];

    for (const [key, value] of entries) {
      const updatedData = removeRows(value.data);

      cacheRef.current.set(key, {
        ...value,
        data: updatedData,
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteAction = async (
    deleteFunction,
    row,
    idField = "id",
    confirmProps = { title: "Confirm Delete", message: "Are you sure?" },
  ) => {
    const { title, message } = confirmProps;
    const keyValue = row[idField];
    if (!keyValue) {
      showNotification("idField provided is not set, please check.", "error");
    }

    const ok = await confirmDialog.show({
      title: title,
      message: message,
    });

    if (!ok) return;

    // delete logic
    try {
      confirmDialog.setLoading(true);
      await deleteFunction(row);
      removeCachedItem(row, idField);
      showNotification("Deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showNotification("Delete failed", "error");
    } finally {
      confirmDialog.setLoading(false);
      confirmDialog.close();
    }
  };

  const addOrDeleteIncludedFeatures = async (newFeature) => {
    const tasks = (newFeature.includedClasses ?? [])
      .map((item, index) => {
        if (!(item.isSelectionNew || item.deleted)) return null;

        const includedFeature = {
          includedFeatureUniqueId: `${item.id}_${newFeature.featureId}`,
          includedFeatureFeatureId: newFeature.featureId,
          includedFeatureClassCode: item.id,
          includedFeatureIndexOrder: 0,
          includedFeatureType: newFeature.featureType,
          includedFeatureIsNew: true,
          includedFeatureIsActive: true,
          includedFeatureIsDefault: false,
        };

        if (item.isSelectionNew && !item.deleted) {
          return {
            index,
            type: "save",
            promise: saveIncludedFeature(includedFeature),
          };
        }

        if (!item.isSelectionNew && item.deleted) {
          return {
            index,
            type: "delete",
            promise: deleteIncludedFeature(includedFeature),
          };
        }

        return null;
      })
      .filter(Boolean);

    const results = await Promise.allSettled(tasks.map((t) => t.promise));
    // index -> outcome of the successful tasks; failed ones keep their row as-is
    // apiRequest hands back the raw Response on a non-2xx instead of throwing
    const succeeded = new Map();
    let failed = 0;
    results.forEach((res, i) => {
      if (
        res.status === "fulfilled" &&
        !(res.value instanceof Response && !res.value.ok)
      ) {
        succeeded.set(tasks[i].index, tasks[i].type);
      } else {
        failed++;
        console.error("Failed:", res.reason ?? res.value);
      }
    });

    const updated = (newFeature.includedClasses ?? []).flatMap((item, index) => {
      const type = succeeded.get(index);
      if (type === "delete") return [];
      if (type === "save") {
        const { deleted, ...rest } = item;
        return [{ ...rest, isSelectionNew: false }];
      }
      return [item];
    });

    // surface partial failures so callers do not report the save as complete
    if (failed > 0) {
      const error = new Error(
        `${failed} class relation change(s) failed to save`,
      );
      error.includedClasses = updated;
      throw error;
    }
    return updated;
  };

  return {
    page,
    rowsPerPage,
    searchTerm,
    searchType,
    setSearchType,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    handleKeyDown,
    data,
    total,
    reload: loadData,
    mutate: updateCachedItem,
    handleDeleteAction,
    addOrDeleteIncludedFeatures,
    fetched,
    isLoading,
    error,
    searchOption,
  };
};
